import { createTheme, responsiveFontSizes, SxProps } from '@mui/material/styles';
import { styled, Theme } from '@mui/material';
import { numberInputClasses } from '@mui/base';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        dark: true;
        grey: true;
        edit: true;
        delete: true;
        view: true;
        white: true;
        default: true;
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        dark: Palette['primary'];
        edit: Palette['primary'];
        delete: Palette['primary'];
        view: Palette['primary'];
        white: Palette['primary'];
        default: Palette['primary'];
    }
    interface PaletteOptions {
        dark: PaletteOptions['primary'];
        edit: PaletteOptions['primary'];
        delete: PaletteOptions['primary'];
        view: PaletteOptions['primary'];
        white: PaletteOptions['primary'];
        default: PaletteOptions['primary'];
    }
}

const defaultFontStyle = {
    typography: {
        fontFamily: 'Inter, Roboto, Arial, sans-serif !important',
    },
};

export const DefaultFontTheme = createTheme(defaultFontStyle);
  
const gridColorTheme = createTheme({
    palette: {
        dark: {
            main: '#444',
            light: '#888',
            dark: '#222',
            contrastText: '#FFF',
        },
        edit: {
            main: '#0046a1',
            light: '#0058c9',
            dark: '#003a85',
            contrastText: '#FFF',
        },
        delete: {
            main: '#c20000',
            light: '#eb0000',
            dark: '#940000',
            contrastText: '#FFF',
        },
        view: {
            main: '#009e3f',
            light: '#00d153',
            dark: '#008234',
            contrastText: '#FFF',
        },
        white: {
            main: '#fff',
            light: '#fafafa',
            dark: '#f1f1f1',
            contrastText: '#444',
        },
        default: {
            main: '#cc1f1f',
            light: '#A11A1A',
            dark: '#B21B1B',
            contrastText: '#fff',
        },
    },
    ...defaultFontStyle,
});

export const GridColorStyles = responsiveFontSizes(gridColorTheme);

export const ButtonGridTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    width: "2rem",
                    height: "2rem",
                    minWidth: `2rem !important`,
                },
            },
        },
    },
});

export const ButtonFormTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    width: "2.5rem",
                    height: "2.5rem",
                    minWidth: `2.5rem !important`,
                    boxShadow: "none",
                },
            },
        },
    },
});

export const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
};
  
export const commonStyle: SxProps<Theme> = {
    width: "100%",
};

export const commonInputStyle: any = {
    InputProps: {
        sx: {
            backgroundColor: "#fff",
            '& .MuiOutlinedInput-notchedOutline': {
                border: "0 !important",
                '& fieldset': {
                    border: "0 !important",
                },
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: "0 !important",
                '& fieldset': {
                    borderRadius: "0 !important",
                },
            },
        },
    },
};

export const textInputStyle: any = {
    sx: {
        '& .MuiOutlinedInput-root': {
            borderWidth: "0 0 2px 0 !important",
            borderStyle: "solid",
            borderColor: "#949494",
            borderRadius: "0 !important",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15);",
            '&.Mui-focused': {
                borderColor: "#cc1f1f",
                borderWidth: "0 0 2px 0 !important",
            },
        },
    }
};

export const textareaInputStyle: any = {
    sx: {
        '& .base-Input-multiline': {
            borderWidth: "0 0 2px 0 !important",
            borderStyle: "solid",
            borderColor: "#949494",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
            borderRadius: "0 !important",
            '&:focus': {
                borderColor: "#cc1f1f",
                borderWidth: "0 0 2px 0 !important",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15) !important",
            },
            '&:hover': {
                borderColor: "#cc1f1f",
            },
        },
    }
};

export const comboboxInputStyle: any = {
    sx: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderWidth: "0 0 2px 0 !important",
                borderStyle: "solid",
                borderColor: "#949494",
                borderRadius: "0 !important",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15);",
            },
            '&.Mui-focused fieldset': {
                borderColor: "#cc1f1f",
                borderWidth: "0 0 3px 0 !important",
            },
        },
    }
};

export const dateInputStyle: any = {
    InputProps: {
        sx: {
            '& .MuiOutlinedInput-input': {
                paddingBottom: "9px",
                paddingTop: "8px",
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: "0 0 2px 0 !important",
                borderStyle: "solid",
                borderColor: "#949494",
                borderRadius: "0 !important",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15);",
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: "0 0 2px 0 !important",
                borderStyle: "solid",
                borderColor: "#cc1f1f",
            },
        },
    },
};

export const StyledInputRoot = styled('div')(
    ({ theme }) => `
        display: grid;
        grid-template-columns: 1fr 19px;
        grid-template-rows: 1fr 1fr;
        overflow: hidden;
        height: 1.2em;
        padding: 8.5px 4px;
        box-sizing: content-box;
        margin-top: 0.135em;
        border-width: 0 0 2px 0 !important;
        border-style: solid;
        border-color: #949494;
        border-radius: 0 !important;
        box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
        &.base--focused {
            border-color: #cc1f1f;
            border-width: 0 0 2px 0 !important;
        }
    `,
);

export const StyledInputElement = styled('input')(
    ({ theme }) => `
    font-size: inherit;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.5;
    grid-column: 1/2;
    grid-row: 1/3;
    background: inherit;
    border: none;
    border-radius: inherit;
    outline: 0;
    height: 1.2em;
`,
);

export const StyledButton = styled('button')(
    ({ theme }) => `
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    appearance: none;
    width: 12px;
    height: 12px;
    font-family: system-ui, sans-serif;
    font-size: 0.7rem;
    box-sizing: border-box;
    border: 0;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 120ms;

    &:hover {
        cursor: pointer;
    }

    &.${numberInputClasses.incrementButton} {
        grid-column: 3/3;
        grid-row: 1/2;
        border: 1px solid #dedede;
        border-bottom: 0;
        background-color: #fff;
    }

    &.${numberInputClasses.decrementButton} {
        grid-column: 3/3;
        grid-row: 2/2;
        border: 1px solid #dedede;
        border-top: 0;
        background-color: #fff;
    }

    &:hover {
        cursor: pointer;
        color: #FFF;
        background-color: #ddd;
    }

    & .arrow {
        transform: translateY(-1px);
    }
`,
);

export const RootDiv = styled('div')`
    display: flex;
    width: 100%;
`;

export const TextareaElement = styled('textarea', {
    shouldForwardProp: (prop) =>
        !['ownerState', 'minRows', 'maxRows'].includes(prop.toString()),
})(
    ({ theme }) => `
    width: 100%;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5rem;
    padding: 8px 12px;
    border-radius: 8px 8px 0 8px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    box-shadow: 0px 2px 4px ${
        theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
    };

    &:focus {
        box-shadow: 0 0 0 3px;
    }

    // firefox
    &:focus-visible {
        outline: 0;
    }
`,
);